import { useMemo, useRef } from 'react';
import Image from '../../components/image/Image';
import { formatMoneyKr, pluralize } from '../../utils';
import { getAvailableRewardCount, isRewardSoldout, rewardHasFreeShipping } from './reward-model';
import ButtonText from '../../components/button/button-text/ButtonText';
import { RewardButtons } from './RewardButtons';
import styles from './reward.module.less';
import { BodyText } from '../../components/typography/Heading';
import RewardModal from './RewardModal';

const PriceInfo = ({ price }) => {
    if (!price) {
        return null;
    }

    return <div className={styles.price}>{formatMoneyKr(price)}</div>;
};

const RewardInfoContent = ({ reward, openModal, hideDefaultImage }) => (
    <>
        <BodyText className={styles.description}>{reward.description}</BodyText>
        {!hideDefaultImage && reward.description && (
            <>
                <ButtonText onClick={openModal} className={styles.readMoreButton}>
                    <div className={styles.readMoreContent}>
                        Les mer
                        <i className={`fa-regular fa-chevron-right`} />
                    </div>
                </ButtonText>
            </>
        )}
    </>
);

const Reward = ({
    reward,
    defaultValue = 1,
    canSelectMultiple = true,
    supportButtonText = 'Legg til',
    min = 1,
    max = 999,
    onClick,
    navigateOnClick,
    onModalOpen,
    hasModalInfo,
    hidePurchasedCount,
    buttonIcon,
    hideDefaultImage,
    hideStock,
}) => {
    const modalRef = useRef();
    const available = getAvailableRewardCount(reward);

    if (supportButtonText === 'Legg til' && navigateOnClick) {
        supportButtonText = 'Kjøp';
    }

    const soldText = useMemo(() => {
        if (hidePurchasedCount) {
            return null;
        }
        if (isRewardSoldout(reward)) {
            return 'Utsolgt!';
        }

        if ((!reward.purchased_count || reward.purchased_count === 0) && available && !hideStock) {
            return `${available} ${pluralize(available, 'tilgjengelig', 'tilgjengelige')}`;
        }

        if (!reward.purchased_count || reward.purchased_count === 0) {
            return null;
        }

        return `${reward.purchased_count}${reward.quantity && !hideStock ? ` av ${reward.quantity}` : ''} solgt`;
    }, [available, hidePurchasedCount, reward]);

    const hasImage = reward.images && reward.images[0].image_url;

    const buttonConfig = {
        available,
        reward,
        onClick,
        defaultValue,
        canSelectMultiple,
        min,
        max,
        navigateOnClick,
        buttonIcon,
        supportButtonText,
    };

    const openModal = () => {
        if (hasModalInfo) {
            onModalOpen(reward.product_reward_category_id, buttonConfig);
        } else {
            modalRef?.current?.open();
        }
    };

    const imageComponent = hasImage ? (
        <Image
            src={reward.images[0].image_url}
            alt="Bilde av belønningen"
            title={reward.title}
            className={styles.image}
            onClick={openModal}
        />
    ) : !hideDefaultImage ? (
        <div className={styles.defaultImageContainer}>
            <Image className={styles.defaultImage} src={`/public/images/product/reward-standard.svg`} />
        </div>
    ) : (
        <div className={styles.noImage} />
    );

    return (
        <div className={styles.reward}>
            <RewardModal
                title={reward.title}
                description={reward.description}
                imageSrc={hasImage ? reward.images[0].image_url : undefined}
                modalRef={modalRef}
                buttonConfig={buttonConfig}
            />
            {imageComponent}

            <div className={styles.rewardHeader}>
                <div className={styles.title}>{reward.title}</div>
                <PriceInfo price={reward.price} hasFreeShipping={rewardHasFreeShipping(reward)} />
                <div className={styles.sold}>{soldText}</div>
            </div>
            <div className={styles.rewardBottom}>
                <div className={styles.mainContent}>
                    <RewardInfoContent reward={reward} openModal={openModal} hideDefaultImage={hideDefaultImage} />
                </div>

                <RewardButtons {...buttonConfig} />
            </div>
        </div>
    );
};

export default Reward;
